import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { updateJob, listTrucks } from "../lib/api";
import {
  EditJobFormFields,
  buildForm,
  buildUpdatePayload,
} from "./editJobSheet/EditJobSheetParts";

export default function EditJobSheet({ job, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(() => buildForm(job));
  // Server values as they were when the sheet opened — the diff baseline.
  const [baseline, setBaseline] = useState(() => buildForm(job));
  const [openSig, setOpenSig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [trucks, setTrucks] = useState([]);

  // Load trucks once the sheet opens — we need them for the truck selector.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    listTrucks()
      .then((t) => { if (!cancelled) setTrucks(t || []); })
      .catch(() => { /* non-fatal — the field just shows empty */ });
    return () => { cancelled = true; };
  }, [open]);

  // Re-seed the form EVERY time the sheet opens (render-phase reset).
  //
  // Jun 2026 — this used to reset only when `job.id` changed, so reopening
  // the SAME job reused whatever was left in state: an abandoned edit
  // reappeared and got saved, and values the poller/another user had
  // changed in the meantime were silently rewritten to the old snapshot.
  // `openSig` flips to "__closed__" on close, so the next open always
  // re-seeds from the current server doc. While the sheet is OPEN the
  // signature doesn't change, so background list refreshes can't wipe
  // what the dispatcher is typing.
  const sig = open ? `open:${job?.id ?? ""}` : "__closed__";
  if (sig !== openSig) {
    setOpenSig(sig);
    if (open) {
      const seeded = buildForm(job);
      setForm(seeded);
      setBaseline(seeded);
    }
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!job) return;
    if (!form.customer_name || !form.address || !form.vehicle) {
      toast.error("Customer name, pickup address and vehicle are required.");
      return;
    }
    const payload = buildUpdatePayload(form, baseline);
    if (Object.keys(payload).length === 0) {
      toast.info("No changes to save");
      onOpenChange(false);
      return;
    }
    setSaving(true);
    try {
      const updated = await updateJob(job.id, payload);
      toast.success("Job updated");
      onOpenChange(false);
      onSaved?.(updated);
      window.dispatchEvent(new Event("jobs:refresh"));
    } catch {
      toast.error("Failed to update job");
    } finally {
      setSaving(false);
    }
  };

  const isAaPlaceholder = job?.source === "aa_sms" && (!job?.address || job.address === "Awaiting details");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg overflow-y-auto"
        data-testid="edit-job-sheet"
      >
        <SheetHeader>
          <SheetTitle className="font-display text-2xl font-black tracking-tight">
            {isAaPlaceholder ? "Complete AA Job Details" : "Edit Job"}
          </SheetTitle>
          <SheetDescription>
            {isAaPlaceholder
              ? `AA Job ${job?.aa_job_id || ""} — fill in the details the dispatcher just heard from AA / the driver.`
              : "Update customer, vehicle, addresses, or notes. Addresses are geocoded automatically."}
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <EditJobFormFields form={form} setForm={setForm} trucks={trucks} />

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => onOpenChange(false)}
              data-testid="edit-job-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="flex-1 bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"
              data-testid="edit-job-submit-btn"
            >
              {saving ? (<><Loader2 className="h-4 w-4 animate-spin" />Saving…</>) : "Save"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
